import React from 'react';
import { isArray, isUndefined, has } from 'lodash';
import classnames from 'classnames';
import { MultiGrid, AutoSizer } from 'react-virtualized';
import { LinearProgress } from '@mui/material';
import {
  VpnKey,
  SkipPrevious as FirstPageIcon,
  ChevronLeft as PrevPageIcon,
  ChevronRight as NextPageIcon,
  SkipNext as LastPageIcon,
} from '@mui/icons-material';
import Tooltip from '../elements/mui/Tooltip';
import MissingValueIcon from '../elements/schema-editor/icons/MissingValueIcon';
import SchemaBrowserSearch from '../elements/schema-editor/schema-browser/SchemaBrowserSearch';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';

import 'react-virtualized/styles.css';
import './DataMapSamples.scss';

const styles = {
  topbarContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingRight: 8,
    height: 38,
    marginBottom: 8,
    border: '1px solid #ccc',
    borderRadius: 4,
  },
  searchBox: {
    container: {
      display: 'flex',
      flexGrow: 1,
    },
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      flexGrow: 1,
      paddingLeft: 8,
      paddingRight: 8,
    },
    searchIcon: {
      color: '#BAC8CC',
      fontSize: 12,
    },
    closeIcon: {
      color: '#BAC8CC',
      cursor: 'pointer',
      fontSize: 12,
    },
    textField: {
      hint: {
        color: '#BAC8CC',
        fontWeight: 'normal',
      },
    },
  },
  progressBarPlaceholder: {
    height: 4,
  },
};

const NUM_SAMPLES = 5;

class DataMapSamples extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
      currentPage: 1,
    };

    this._handleFirstPageClicked = this._handleFirstPageClicked.bind(this);
    this._handleLastPageClicked = this._handleLastPageClicked.bind(this);
    this._handlePrevPageClicked = this._handlePrevPageClicked.bind(this);
    this._handleNextPageClicked = this._handleNextPageClicked.bind(this);
    this._handleSearchChanged = this._handleSearchChanged.bind(this);
    this._getRowHeight = this._getRowHeight.bind(this);
    this._getRowAttrs = this._getRowAttrs.bind(this);
    this._cellRenderer = this._cellRenderer.bind(this);
    this._forceUpdateBrowser = this._forceUpdateBrowser.bind(this);
  }

  componentDidMount() {
    this.props.clearDataMapSearchResults();
    if (!this.props.preventInitialFetch) {
      this.props.loadDataMap(this.props.data.id, {
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        expand: true,
      });
    }
    this.props.loadDataMapSamples(this.props.data.id, 1, NUM_SAMPLES);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.currentPage !== this.state.currentPage) {
      this.props.loadDataMapSamples(this.props.data.id, this.state.currentPage, NUM_SAMPLES);
    }

    if (
      prevProps.data !== this.props.data ||
      prevProps.dataMapSamples !== this.props.dataMapSamples ||
      prevProps.dataMapSearchResults !== this.props.dataMapSearchResults
    ) {
      //TODO:R16: Replace with faster comparisions
      this._forceUpdateBrowser();
    }
  }

  _handleFirstPageClicked() {
    this.setState({ currentPage: 1 });
  }

  _handleLastPageClicked() {
    this.setState({ currentPage: this._getTotalPages() });
  }

  _handlePrevPageClicked() {
    const { currentPage } = this.state;

    this.setState({ currentPage: Math.max(1, currentPage - 1) });
  }

  _handleNextPageClicked() {
    const { currentPage } = this.state;

    this.setState({
      currentPage: Math.min(this._getTotalPages(), currentPage + 1),
    });
  }

  _handleSearchChanged(searchText) {
    this.setState({ searchText }, () => {
      if (searchText) {
        this.props.loadDataMapEntry(this.props.data.id, searchText);
      } else {
        this.props.clearDataMapSearchResults();
      }
    });
  }

  _getTotalPages() {
    const {
      data: { map_entry_count },
    } = this.props;

    return Math.max(1, Math.ceil((map_entry_count || 0) / NUM_SAMPLES));
  }

  _getRowHeight({ index }) {
    // if the record in this column is missing
    if (this._getRowAttrs(index) === null) {
      return 20;
    }

    return this.props.rowHeight;
  }

  _getRowAttrs(rowIndex) {
    const { dataMapSamples, dataMapSearchResults } = this.props;
    const effectiveRowIndex = rowIndex - 1;
    const rowsData = dataMapSearchResults || dataMapSamples;

    return rowsData ? rowsData[effectiveRowIndex] : undefined;
  }

  _forceUpdateBrowser() {
    if (this._grid && typeof this._grid !== 'undefined') {
      this._grid.recomputeGridSize();
      this._grid.forceUpdateGrids();
    }
  }

  _cellRenderer(cell) {
    return cell.rowIndex === 0 ? this._frozenCellRenderer(cell) : this._freeCellRenderer(cell);
  }

  _frozenCellRenderer({ columnIndex, key, rowIndex, style }) {
    const {
      data: { map_primary_key },
      properties,
    } = this.props;
    const attributeName = properties[columnIndex];
    const isPrimary = attributeName === map_primary_key;

    return (
      <div
        key={key}
        className="tableCell AttrInfoCell"
        style={{ ...style, gap: 'var(--spacing-md)', padding: 0 }}
      >
        <span className="attrName" title={attributeName}>
          {attributeName}
        </span>
        {isPrimary && (
          <VpnKey
            fontSize="small"
            style={{ paddingLeft: 'var(--spacing-xs)', color: '#adadad', flexShrink: 0 }}
          />
        )}
      </div>
    );
  }

  _freeCellRenderer({ columnIndex, key, rowIndex, style }) {
    const { dataMapSamples, properties } = this.props;
    const rowAttrs = this._getRowAttrs(rowIndex);
    const attrId = properties[columnIndex];
    let className = 'tableCell sampCell';
    let sampleContent = <div className="dataCell" />;

    // if samples have been loaded
    if (dataMapSamples) {
      // if the sample record exists
      if (rowAttrs) {
        let valueExists = has(rowAttrs, attrId);

        if (valueExists) {
          const fetchedSample = isUndefined(rowAttrs[attrId]) ? '' : rowAttrs[attrId];

          sampleContent = (
            <div className="dataCell">
              {isArray(fetchedSample) ? `[${fetchedSample}]` : `${fetchedSample}`}
              <br />
            </div>
          );

          // if value is null, update cell class
          if (fetchedSample === null || isUndefined(fetchedSample)) {
            className = classnames(className, 'nullValue');
          }
        } else {
          className = classnames(className, 'missingValue');
          sampleContent = (
            <Tooltip className="cellToolTip" title="Attribute missing in record" arrow={true}>
              <MissingValueIcon />
            </Tooltip>
          );
        }
      } else {
        className = classnames(className, 'missingSample');
        sampleContent = (
          <Tooltip className="cellToolTip" title="Record filtered out" arrow={true}>
            {sampleContent}
          </Tooltip>
        );
      }
    }

    return (
      <div className={className} key={key} style={style}>
        {sampleContent}
      </div>
    );
  }

  render() {
    const { isLoading, dataMapSamples, dataMapSearchResults, properties, minColWidth } = this.props;
    const { searchText, currentPage } = this.state;
    const colCount = properties.length;
    const samplesCount = (dataMapSearchResults || dataMapSamples).length;
    const rowCount = samplesCount + 1;
    const isPrevPageEnabled = currentPage > 1;
    const isNextPageEnabled = rowCount >= NUM_SAMPLES;

    return (
      <div className="data-map-samples">
        <div className="topbar-container" style={styles.topbarContainer}>
          <div className="search-container" style={styles.searchBox.container}>
            <SchemaBrowserSearch
              hintText="Search records by primary key"
              searchText={searchText}
              style={styles.searchBox}
              onChange={this._handleSearchChanged}
            />
          </div>

          <div className="pagination-container">
            <span className="pagination-button" onClick={this._handleFirstPageClicked}>
              <FirstPageIcon fontSize="large" color={isPrevPageEnabled ? 'inherit' : 'disabled'} />
            </span>
            <span className="pagination-button" onClick={this._handlePrevPageClicked}>
              <PrevPageIcon fontSize="large" color={isPrevPageEnabled ? 'inherit' : 'disabled'} />
            </span>
            <span className="total-page-count">
              {currentPage} of {this._getTotalPages()}
            </span>
            <span className="pagination-button" onClick={this._handleNextPageClicked}>
              <NextPageIcon fontSize="large" color={isNextPageEnabled ? 'inherit' : 'disabled'} />
            </span>
            <span className="pagination-button" onClick={this._handleLastPageClicked}>
              <LastPageIcon fontSize="large" color={isNextPageEnabled ? 'inherit' : 'disabled'} />
            </span>
          </div>
        </div>

        {(isLoading && <LinearProgress variant="query" className="progress" />) || (
          <div style={styles.progressBarPlaceholder} />
        )}

        {!isLoading &&
          ((samplesCount === 0 && 'No records found.') || (
            <AutoSizer>
              {({ width, height }) => (
                <MultiGrid
                  ref={(g) => {
                    this._grid = g;
                  }}
                  width={width}
                  height={height}
                  cellRenderer={this._cellRenderer}
                  columnCount={colCount}
                  columnWidth={Math.max(minColWidth, width / colCount)}
                  rowCount={rowCount}
                  rowHeight={this._getRowHeight}
                  scrollToRow={15}
                  enableFixedColumnScroll
                  enableFixedRowScroll
                  fixedRowCount={1}
                />
              )}
            </AutoSizer>
          ))}
      </div>
    );
  }
}

DataMapSamples.defaultProps = {
  rowHeight: 40,
  colCount: 0,
  minColWidth: 150,
};

export default DataMapSamples;
